import React, { useState, useEffect } from 'react';
import { adminSubscriptionsAPI } from '../../services/adminAPI';
import { toast } from 'react-toastify';

const statusColors = {
  active: { bg: '#d1fae5', color: '#065f46' },
  expired: { bg: '#fee2e2', color: '#991b1b' },
  cancelled: { bg: '#fef3c7', color: '#92400e' },
  pending: { bg: '#e0e7ff', color: '#3730a3' },
};

const SubscriptionManagement = () => {
  const [stats, setStats] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filterStatus, setFilterStatus] = useState('');
  const [search, setSearch] = useState('');
  const [runningCheck, setRunningCheck] = useState(false);

  const loadStats = async () => {
    try {
      const res = await adminSubscriptionsAPI.getStats();
      setStats(res.data);
    } catch { /* silent */ }
  };

  const load = async (p = 1) => {
    setLoading(true);
    try {
      const params = { page: p, per_page: 20 };
      if (filterStatus) params.status = filterStatus;
      if (search) params.search = search;
      const res = await adminSubscriptionsAPI.getAll(params);
      setSubscriptions(res.data.subscriptions || []);
      setTotalPages(res.data.pages || 1);
      setPage(p);
    } catch {
      toast.error('Failed to load subscriptions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadStats(); load(1); }, [filterStatus]);

  const handleRunCheck = async () => {
    setRunningCheck(true);
    try {
      await adminSubscriptionsAPI.runCheck();
      toast.success('Subscription check completed!');
      load(page);
      loadStats();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Check failed');
    } finally {
      setRunningCheck(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    load(1);
  };

  return (
    <div style={s.page}>
      <div style={s.topBar}>
        <div>
          <h1 style={s.heading}>💳 Subscription Management</h1>
          <p style={s.subheading}>Monitor and manage all user subscriptions</p>
        </div>
        <button
          style={{ ...s.runBtn, opacity: runningCheck ? 0.7 : 1 }}
          onClick={handleRunCheck}
          disabled={runningCheck}
        >
          {runningCheck ? '⏳ Running…' : '▶ Run Expiry Check Now'}
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div style={s.statsRow}>
          {[
            { label: 'Total', value: stats.total, color: '#6c63ff' },
            { label: 'Active', value: stats.active, color: '#059669' },
            { label: 'Expired', value: stats.expired, color: '#dc2626' },
            { label: 'Cancelled', value: stats.cancelled, color: '#d97706' },
            { label: 'Total Revenue', value: `₦${(stats.total_revenue || 0).toLocaleString()}`, color: '#0891b2' },
          ].map(({ label, value, color }) => (
            <div key={label} style={s.statCard}>
              <div style={{ ...s.statValue, color }}>{value}</div>
              <div style={s.statLabel}>{label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div style={s.filterBar}>
        <form onSubmit={handleSearch} style={{ display: 'flex', gap: '8px', flex: 1 }}>
          <input
            style={s.searchInput}
            placeholder="Search by email or username…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <button style={s.searchBtn} type="submit">Search</button>
        </form>
        <select style={s.select} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="">All Statuses</option>
          <option value="active">Active</option>
          <option value="expired">Expired</option>
          <option value="cancelled">Cancelled</option>
          <option value="pending">Pending</option>
        </select>
      </div>

      {/* Table */}
      <div style={s.tableWrap}>
        {loading ? (
          <div style={s.loader}>Loading…</div>
        ) : subscriptions.length === 0 ? (
          <div style={s.loader}>No subscriptions found</div>
        ) : (
          <table style={s.table}>
            <thead>
              <tr style={s.thead}>
                {['User', 'Plan', 'Status', 'Amount', 'Billing', 'Expires', 'Days Left'].map(h => (
                  <th key={h} style={s.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {subscriptions.map(sub => {
                const sc = statusColors[sub.status] || { bg: '#f3f4f6', color: '#374151' };
                return (
                  <tr key={sub.id} style={s.tr}>
                    <td style={s.td}>
                      <div style={{ fontWeight: 600, fontSize: '13px' }}>{sub.user_name || '—'}</div>
                      <div style={{ fontSize: '11px', color: '#9ca3af' }}>{sub.user_email}</div>
                    </td>
                    <td style={s.td}>{sub.plan}</td>
                    <td style={s.td}>
                      <span style={{ ...s.badge, background: sc.bg, color: sc.color }}>{sub.status}</span>
                    </td>
                    <td style={s.td}>₦{(sub.amount_paid || 0).toLocaleString()}</td>
                    <td style={s.td}>{sub.billing_cycle}</td>
                    <td style={s.td}>{sub.expires_at ? new Date(sub.expires_at).toLocaleDateString() : '—'}</td>
                    <td style={s.td}>
                      <span style={{ color: sub.days_remaining <= 3 ? '#dc2626' : '#059669', fontWeight: 600 }}>
                        {sub.days_remaining}d
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={s.pagination}>
          <button style={s.pageBtn} onClick={() => load(page - 1)} disabled={page === 1}>‹ Prev</button>
          <span style={{ color: '#6b7280', fontSize: '13px' }}>Page {page} of {totalPages}</span>
          <button style={s.pageBtn} onClick={() => load(page + 1)} disabled={page === totalPages}>Next ›</button>
        </div>
      )}
    </div>
  );
};

const s = {
  page: { padding: '24px', maxWidth: '1200px' },
  topBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' },
  heading: { margin: 0, fontSize: '24px', fontWeight: 700, color: '#1f2937' },
  subheading: { margin: '4px 0 0', color: '#6b7280', fontSize: '14px' },
  runBtn: { background: '#059669', color: '#fff', border: 'none', borderRadius: '8px', padding: '10px 18px', cursor: 'pointer', fontWeight: 600, fontSize: '13px' },
  statsRow: { display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' },
  statCard: { flex: '1 1 130px', background: '#fff', borderRadius: '12px', padding: '16px', border: '1px solid #e5e7eb', textAlign: 'center' },
  statValue: { fontSize: '22px', fontWeight: 800, marginBottom: '4px' },
  statLabel: { fontSize: '12px', color: '#6b7280', fontWeight: 500 },
  filterBar: { display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' },
  searchInput: { flex: 1, padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '13px', minWidth: '180px', outline: 'none' },
  searchBtn: { background: '#6c63ff', color: '#fff', border: 'none', borderRadius: '8px', padding: '9px 16px', cursor: 'pointer', fontWeight: 600, fontSize: '13px' },
  select: { padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '13px', outline: 'none' },
  tableWrap: { background: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb', overflow: 'auto' },
  loader: { textAlign: 'center', color: '#888', padding: '48px' },
  table: { width: '100%', borderCollapse: 'collapse', minWidth: '700px' },
  thead: { background: '#f9fafb' },
  th: { padding: '12px 14px', textAlign: 'left', fontSize: '12px', fontWeight: 600, color: '#6b7280', borderBottom: '1px solid #e5e7eb' },
  tr: { borderBottom: '1px solid #f3f4f6' },
  td: { padding: '12px 14px', fontSize: '13px', color: '#374151', verticalAlign: 'middle' },
  badge: { display: 'inline-block', padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 600 },
  pagination: { display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '16px', marginTop: '16px' },
  pageBtn: { background: '#fff', border: '1px solid #d1d5db', borderRadius: '8px', padding: '7px 14px', cursor: 'pointer', fontSize: '13px' },
};

export default SubscriptionManagement;
